import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icon';
import {
  ANALYTICS_EVENTS,
  AXIS_IDS,
  MERCHANT,
  band,
  TIER_PRICE,
  getContent,
  subjectTitle,
  zoneText,
  type PairViewDto,
  type PairTeaserDto,
  type PairReportDto,
  type Tier,
  type Content,
} from '@neke/core';
import type { ApiError } from '../api/client';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { AxisRow } from '../components/AxisRow';
import { ReportFile } from '../components/ReportFile';
import { track } from '../analytics';

/** Сервер присылает коды и параметры — предложение собирается здесь, по локали. */
export function PairReport() {
  const { t } = useTranslation();
  // Ссылка-приглашение приходит как /pair/:token, возврат после оплаты —
  // как /pair/id/:pairId: принимать приглашение второй раз незачем.
  const { token, pairId } = useParams();
  const navigate = useNavigate();
  const { locale, ready, user } = useSession();
  const content = getContent(locale);

  const [view, setView] = useState<PairViewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [granting, setGranting] = useState(false);
  const [granted, setGranted] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  const isAdmin = user?.role === 'ADMIN';
  // openGrant разбирает и id, и токен приглашения — отдаём то, что есть в адресе.
  const pairRef = pairId ?? token;

  useEffect(() => {
    if (!ready) return;
    const path = pairId ? `/pair/id/${pairId}` : `/pair/${token}`;
    if (!user) {
      window.location.href = `/login?next=${path}`;
      return;
    }
    const load = pairId ? api.pairReport(pairId) : token ? api.acceptInvite(token) : null;
    if (!load) return;
    // Приглашение принимается только на входе по ссылке — это отдельная
    // ступень воронки: партнёр дошёл до пары.
    const entry = pairId ? 'direct' : 'invite';
    void load
      .then((next) => {
        setView(next);
        void track(ANALYTICS_EVENTS.pairReportViewed, {
          entry,
          access: next.access,
          locked: next.teaser?.locked ?? 0,
          green: next.green,
          yellow: next.yellow,
          red: next.red,
        });
        if (next.access !== 'full' && next.teaser) {
          void track(ANALYTICS_EVENTS.paywallViewed, {
            locked: next.teaser.locked,
            tiers_available: next.offer?.tiers.length ?? 0,
          });
        }
      })
      .catch((e: ApiError) => {
        // Приглашение уже принято — своей анкеты просто нет. Не показываем тупик,
        // а сразу отправляем на вопросы и возвращаем сюда после завершения.
        if (e.code === 'no_profile_self') {
          void track(ANALYTICS_EVENTS.pairNeedsOwnTest, { entry });
          navigate(`/test?next=${encodeURIComponent(path)}`, { replace: true });
          return;
        }
        setError(e.message);
        void track(ANALYTICS_EVENTS.pairReportFailed, { entry, code: e.code });
      });
  }, [ready, token, pairId, user, navigate]);

  /**
   * Админ открывает доступ прямо со страницы отчёта — без похода в админку и
   * копирования ссылки. Чужую пару он по-прежнему не читает: сервер пускает в
   * отчёт только участников, поэтому после выдачи гранта перечитываем витрину
   * и молча остаёмся на месте, если она снова закрыта.
   */
  const openAccess = () => {
    if (!pairRef) return;
    setGranting(true);
    setGrantError(null);
    void api
      .adminReportOpen(pairRef)
      .then((grant) => {
        setGranted(true);
        void track(ANALYTICS_EVENTS.adminReportOpened, { from: 'pair_report' });
        return api
          .pairReport(grant.pairId)
          .then((next) => {
            setView(next);
            setError(null);
          })
          .catch(() => undefined);
      })
      .catch((e: ApiError) => setGrantError(e.message))
      .finally(() => setGranting(false));
  };

  const adminGrant = isAdmin && pairRef && (
    <AdminGrant busy={granting} done={granted} error={grantError} onOpen={openAccess} />
  );

  if (error)
    return (
      <div className="page">
        <div className="card">
          <p>{error}</p>
          {adminGrant}
        </div>
      </div>
    );
  if (!view)
    return (
      <div className="page">
        <p className="muted">{t('common.loading')}</p>
      </div>
    );

  const partnerName = view.partnerName;

  const buy = (tier: Tier) => {
    setBuying(true);
    void track(ANALYTICS_EVENTS.checkoutStarted, {
      tier,
      price: view.offer?.tiers.find((o) => o.tier === tier)?.price ?? TIER_PRICE[tier],
      locked: view.teaser?.locked ?? 0,
      // Тот же шаг воронки есть в приложении, но платят там через стор и не
      // всегда в тенге. Проставляем канал и валюту, чтобы суммы не смешались.
      provider: 'kaspi',
      currency: view.offer?.currency ?? 'KZT',
    });
    void api
      .startPayment(view.pairId, tier)
      .then((payment) => {
        // Ссылка Kaspi живёт минуты: уводим на оплату сразу, а экран ожидания
        // остаётся здесь и сам подхватит результат.
        navigate(`/payment/${payment.id}`, { state: { link: payment.paymentLink } });
      })
      .catch((e: ApiError) => {
        setError(e.message);
        setBuying(false);
        void track(ANALYTICS_EVENTS.checkoutFailed, { tier, code: e.code });
      });
  };

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('pair.title')}</h1>
        <p className="muted">{t('pair.lead')}</p>
        <p className="fine side-keys">
          <span className="side-key a">
            <Icon name="dot" size={9} /> {view.a.name}
          </span>
          <span className="side-key b">
            <Icon name="dot" size={9} /> {view.b.name}
          </span>
        </p>
      </div>

      <Summary green={view.green} yellow={view.yellow} red={view.red} />

      {view.access === 'full' && view.report ? (
        <>
          <FullReport report={view.report} content={content} />
          <div className="card stack">
            <h3>{t('report.pdfTitle')}</h3>
            <p className="muted">{t('report.pdfLead', { name: partnerName })}</p>
            <ReportFile pairId={view.pairId} />
          </div>
        </>
      ) : (
        view.teaser && (
          <>
            <Teaser teaser={view.teaser} content={content} />
            {/* Пара уже купила разбор. Доступ личный, передать его нельзя —
                но попросить готовый файл дешевле, чем платить второй раз. */}
            {view.partnerPaid && (
              <p className="notice">{t('paywall.partnerPaid', { name: partnerName })}</p>
            )}
            <Paywall
              pairId={view.pairId}
              locked={view.teaser.locked}
              tiers={view.offer?.tiers ?? []}
              busy={buying}
              onBuy={buy}
            />
            {adminGrant}
          </>
        )
      )}
    </div>
  );
}

/** Кнопка видна только ADMIN: остальных сервер всё равно завернёт на 403. */
function AdminGrant({
  busy,
  done,
  error,
  onOpen,
}: {
  busy: boolean;
  done: boolean;
  error: string | null;
  onOpen: () => void;
}) {
  const { t } = useTranslation();

  // Доступ выдан, но страница не перечиталась — значит пара чужая. Отчёт админу
  // не покажем, а участники увидят полный разбор при следующем открытии.
  if (done)
    return (
      <p className="fine block-gap">
        {t('admin.reportOpened')} <Link to="/admin">{t('nav.admin')}</Link>
      </p>
    );

  return (
    <div className="stack block-gap">
      <button className="btn btn-ghost" disabled={busy} onClick={onOpen}>
        {busy ? t('admin.reportOpening') : t('admin.reportOpen')}
      </button>
      <p className="fine">{error ?? t('admin.reportOpenHint')}</p>
    </div>
  );
}

function Summary({ green, yellow, red }: { green: number; yellow: number; red: number }) {
  const { t } = useTranslation();
  return (
    <div className="card summary">
      <div className="summary-row green">
        <b>{green}</b> <span>{t('pair.summaryGreen')}</span>
      </div>
      <div className="summary-row yellow">
        <b>{yellow}</b> <span>{t('pair.summaryYellow')}</span>
      </div>
      <div className="summary-row red">
        <b>{red}</b> <span>{t('pair.summaryRed')}</span>
      </div>
    </div>
  );
}

const TIER_KEYS = [
  { key: 'green' as const, title: 'pair.green' },
  { key: 'yellow' as const, title: 'pair.yellow' },
  { key: 'red' as const, title: 'pair.red' },
];

/**
 * Бесплатная часть: совпадения читаются целиком, различия — только названием
 * сферы. Формулировок закрытых зон здесь нет и в ответе сервера тоже.
 */
function Teaser({ teaser, content }: { teaser: PairTeaserDto; content: Content }) {
  const { t } = useTranslation();

  return (
    <>
      {TIER_KEYS.map(({ key, title }) => {
        const zones = teaser.zones.filter((z) => z.tier === key);
        if (zones.length === 0) return null;
        return (
          <div className="stack" key={key}>
            <h3>{t(title)}</h3>
            {zones.map((zone, i) => {
              const name = subjectTitle(zone.subject, content);
              if (zone.locked) {
                return (
                  <div className={`zone ${key} locked`} key={`${key}-${i}`}>
                    <span className="tag">{t(title)}</span>
                    <span className="locked-name">{name ?? t('pair.lockedZone')}</span>
                    <span className="lock" aria-label={t('pair.locked')}>
                      🔒
                    </span>
                  </div>
                );
              }
              return (
                <div className={`zone ${key}`} key={`${key}-${i}`}>
                  <span className="tag">{t(title)}</span>
                  <span>{zoneText(zone, content)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function Paywall({
  pairId,
  locked,
  tiers,
  busy,
  onBuy,
}: {
  pairId: string;
  locked: number;
  tiers: Array<{ tier: Tier; price: number }>;
  busy: boolean;
  onBuy: (tier: Tier) => void;
}) {
  const { t } = useTranslation();
  const money = (value: number) => `${value.toLocaleString('ru-RU')} ₸`;

  // Пустой список тарифов означает, что Kaspi не подключён. Тупик вместо
  // покупки не годится: предлагаем написать нам и открываем отчёт вручную.
  const reportUrl = `${window.location.origin}/pair/id/${pairId}`;
  const waHref = `https://wa.me/${MERCHANT.whatsapp}?text=${encodeURIComponent(
    t('paywall.waText', { link: reportUrl }),
  )}`;

  return (
    <div className="card paywall">
      <h2>{t('paywall.title')}</h2>
      <p className="muted">{t('paywall.lead', { count: locked })}</p>
      <ul className="paywall-list">
        <li>{t('paywall.item1')}</li>
        <li>{t('paywall.item2')}</li>
        <li>{t('paywall.item3')}</li>
      </ul>
      {tiers.length === 0 ? (
        <>
          <h3>{t('paywall.manualTitle')}</h3>
          {/* Именно ссылка, а не кнопка: нужен обычный переход в приложение. */}
          <a
            className="btn btn-gold"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => void track(ANALYTICS_EVENTS.manualCheckoutStarted, { channel: 'whatsapp', locked })}
          >
            {t('paywall.waButton')}
          </a>
          <p className="fine">{t('paywall.manualNote')}</p>
        </>
      ) : (
        tiers.map(({ tier, price }) => (
          <button key={tier} className="btn btn-gold" disabled={busy} onClick={() => onBuy(tier)}>
            {busy
              ? t('paywall.opening')
              : t('paywall.buy', { price: money(price ?? TIER_PRICE[tier]) })}
          </button>
        ))
      )}
      <p className="fine">{t('paywall.note')}</p>
      {/* Оферта должна быть видна там, где человек платит, а не только в подвале. */}
      <p className="fine">
        <Link to="/offer">{t('paywall.offerLink')}</Link>
      </p>
    </div>
  );
}

function FullReport({ report, content }: { report: PairReportDto; content: Content }) {
  const { t } = useTranslation();

  const tiers = [
    { key: 'red' as const, title: t('pair.red') },
    { key: 'yellow' as const, title: t('pair.yellow') },
    { key: 'green' as const, title: t('pair.green') },
  ];

  return (
    <>
      <div className="card">
        {AXIS_IDS.map((axis) => (
          <AxisRow
            key={axis}
            content={content.axes[axis]}
            value={report.a.profile.axes[axis]}
            partnerValue={report.b.profile.axes[axis]}
            partnerName={report.b.name}
            // Пояснение о своей позиции — то же, что в личном отчёте: без него
            // строка «ближе к…» остаётся ярлыком без смысла.
            note={content.axes[axis][band(report.a.profile.axes[axis])]}
          />
        ))}
      </div>

      {tiers.map(({ key, title }) => {
        const zones = report.zones.filter((z) => z.tier === key);
        if (zones.length === 0) return null;
        return (
          <div className="stack" key={key}>
            <h3>{title}</h3>
            {zones.map((zone, i) => (
              <div className={`zone ${key}`} key={`${zone.code}-${i}`}>
                <span className="tag">{title}</span>
                <span>{zoneText(zone, content)}</span>
                {zone.talk && zone.talk.length > 0 && (
                  <>
                    <span className="fine">{t('pair.talk')}</span>
                    <ul>
                      {zone.talk.map((key) => (
                        <li key={key}>{content.talk[key] ?? key}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}
